import React, { FC, useEffect, useRef } from 'react'
import cx from 'classnames'
import {
  useGlobalStorages,
  useIsStorageWritable,
  useOptionsForWritableStorages
} from '@features/storages'
import styled from 'styled-components'

import { FeedbotCreatableSelect, FeedbotCreatableSelectProps } from '../FeedbotCreatableSelect'
import { useBotVariables } from '../../../../store/flow'
import {
  evaluateFormatAdherence,
  getFormatAdherenceError
} from '../../../../lib/rjsf-validation/validationSchema'
import { slugifyStorage } from '../../../../lib/storage'
import { toSelectOption } from '../../../../lib/options'

type Props = Omit<FeedbotCreatableSelectProps, 'options'> & {
  selectedStorageName?: string | null
  preventChangeOnEmptyInputBackspace?: boolean
}

export const WritableStorageSelect: FC<Props> = ({
  selectedStorageName,
  preventChangeOnEmptyInputBackspace,
  ...rest
}) => {
  const botVariables = useBotVariables()
  const globalStorages = useGlobalStorages()
  const writableStoragesOptions = useOptionsForWritableStorages()
  const isPressingBackspace = useRef(false)
  const isSelectedStorageWritable = useIsStorageWritable()(selectedStorageName)

  useEffect(() => {
    isPressingBackspace.current = false
  })

  return (
    <FeedbotCreatableSelect
      options={writableStoragesOptions}
      placeholder="Enter existing or new storage"
      isValidNewOption={inputValue => {
        const lowercaseInputValue = inputValue.toLowerCase()

        const additionalParams = { botVariables, globalStorages }

        return (
          !!inputValue &&
          evaluateFormatAdherence('writableStorageName', inputValue, additionalParams) &&
          !writableStoragesOptions.some(o => o.value.toLowerCase() === lowercaseInputValue)
        )
      }}
      formatCreateLabel={inputValue => `Create storage "${slugifyStorage(inputValue)}"`}
      formatInvalidCreateLabel={inputValue => {
        if (!inputValue) {
          return ''
        }

        const additionalParams = { botVariables, globalStorages }

        const error = getFormatAdherenceError('writableStorageName', inputValue, additionalParams)
        return error && <InvalidCreateLabel>Cannot create new storage: {error}</InvalidCreateLabel>
      }}
      components={{ NoOptionsMessage: () => null }}
      isClearable={true}
      createOptionPosition="first"
      {...rest}
      onKeyDown={event => {
        if (event.key === 'Backspace') {
          isPressingBackspace.current = true
        }

        rest.onKeyDown?.(event)
      }}
      onChange={(option, meta) => {
        if (
          meta.action === 'clear' &&
          preventChangeOnEmptyInputBackspace &&
          isPressingBackspace.current
        ) {
          return
        }

        rest.onChange?.(option, meta)
      }}
      data-test={cx('storage-select', rest['data-test'])}
      value={isSelectedStorageWritable ? toSelectOption(selectedStorageName!) : null}
    />
  )
}

const InvalidCreateLabel = styled.span`
  color: red;
`

export type WritableStorageSelectProps = Props
